import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../api/auth';
import { chatAPI } from '../api/chat';
import OfficialBadge from '../components/OfficialBadge';

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await authAPI.getUserProfile(username);
        setProfile(data);
      } catch (err) {
        setError('Пользователь не найден');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [username]);

  const handleCopyMiraLink = async () => {
    if (!profile?.username) return;

    const link = `${window.location.origin}/#/MiraLink/${profile.username}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopyStatus('Ссылка скопирована');
    } catch {
      setCopyStatus('Не удалось скопировать ссылку');
    }

    setTimeout(() => setCopyStatus(''), 2000);
  };

  const handleStartChat = async () => {
    setCreatingChat(true);
    try {
      const token = localStorage.getItem('token');
      const conversation = await chatAPI.createConversation(token, profile.id);
      navigate(`/chat/${conversation.id}`);
    } catch (err) {
      console.error('Failed to create chat:', err);
      alert('Не удалось создать чат');
    } finally {
      setCreatingChat(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <p className="text-red-600 text-xl mb-4">{error}</p>
          <button onClick={() => navigate('/messenger')} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            Назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/messenger')} className="text-gray-600 hover:text-gray-800">
            ← Назад
          </button>
          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Профиль</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            {profile?.avatar ? (
              <img src={profile.avatar} alt={profile.username} className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-purple-200" />
            ) : (
              <div className="w-32 h-32 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mx-auto flex items-center justify-center text-white text-5xl font-bold mb-4">
                {profile?.username?.charAt(0).toUpperCase()}
              </div>
            )}
            {profile?.nickname && <h2 className="text-3xl font-bold mb-1">{profile.nickname}</h2>}
            <div className="text-xl text-gray-600 mb-2 flex items-center justify-center gap-2">
              <span>@{profile?.username}</span>
              <OfficialBadge username={profile?.username} />
            </div>
            {profile?.bio && <p className="text-gray-600 mt-4 text-lg">{profile.bio}</p>}
          </div>

          <div className="mt-8 space-y-3">
            <button
              onClick={handleCopyMiraLink}
              className="w-full bg-white border border-purple-300 text-purple-700 font-semibold py-3 rounded-lg hover:bg-purple-50 transition"
              data-testid="copy-miralink-button"
            >
              Скопировать ссылку MiraLink
            </button>

            {copyStatus && <p className="text-sm text-center text-gray-600">{copyStatus}</p>}

            <button
              onClick={handleStartChat}
              disabled={creatingChat}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition disabled:opacity-50"
              data-testid="send-message-button"
            >
              {creatingChat ? 'Создание чата...' : 'Написать сообщение'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
